import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useFormik } from "formik";
import * as Yup from "yup";
import Swal from "sweetalert2";
import Breadcrums from "../../common/breadcrumbs";
import { rewardConfigAdd } from "../../services/reward-config.services";
import { SWAL_SETTINGS } from "../../../../utils/Constants";
import { handleServerValidations } from "../../../../utils/commonfunction";
import CustomError from "../../../../utils/customError";

const RewardConfigAdd = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [loader, setLoader] = useState(false);

  const breadcrumbs = [
    { title: t("link_dashboard"), url: "/admin/dashboard" },
    {
      title: t("Reward Management"),
      url: "/admin/reward-management/reward-config/list/1",
    },
    { title: t("Add Reward Config"), url: "" },
  ];

  const validationSchema = Yup.object().shape({
    type: Yup.string().required(t("Type is required")),
    title: Yup.string().trim().required(t("Title is required")),
    description: Yup.string().trim(),
    reward_type: Yup.string().required(t("Reward Type is required")),
    value: Yup.string().required(t("Value is required")),
    criteria: Yup.object().shape({
      min_score: Yup.number().nullable(),
      min_accuracy: Yup.number().nullable(),
      streak_days: Yup.number().nullable(),
      total_games: Yup.number().nullable(),
      milestone_type: Yup.string().nullable(),
    }),
  });

  const formik = useFormik({
    initialValues: {
      type: "",
      title: "",
      description: "",
      reward_type: "",
      value: "",
      criteria: {
        min_score: "",
        min_accuracy: "",
        streak_days: "",
        total_games: "",
        milestone_type: "",
      },
    },
    validationSchema,
    onSubmit: (values) => {
      setLoader(true);
      rewardConfigAdd(values)
        .then((response) => {
          setLoader(false);
          if (response.success) {
            Swal.fire({
              icon: "success",
              text: response.message,
              ...SWAL_SETTINGS,
            }).then(() => {
              navigate("/admin/reward-management/reward-config/list/1");
            });
          } else {
            Swal.fire({
              icon: "error",
              text: handleServerValidations(response),
              ...SWAL_SETTINGS,
            });
          }
        })
        .catch((error) => {
          setLoader(false);
          console.log("error", error);
        });
    },
  });

  const selectedType = formik.values.type;

  return (
    <>
      <Breadcrums data={breadcrumbs} />
      <div className="animation_fade">
        <div className="card custom-card">
          <div className="card-header border-bottom">
            <h6 className="main-content-label mb-0">
              {t("Add Reward Config")}
            </h6>
          </div>
          <div className="card-body">
            <form onSubmit={formik.handleSubmit}>
              <div className="row">
                <div className="col-md-6 form-group">
                  <label className="text-left d-flex">
                    {t("Type")} <span className="text-danger">*</span>
                  </label>
                  <select
                    name="type"
                    className="form-control"
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    value={formik.values.type}
                  >
                    <option value="">{t("Select Type")}</option>
                    <option value="level_completion">Level Completion</option>
                    <option value="streak_bonus">Streak Bonus</option>
                    <option value="milestone_achievement">
                      Milestone Achievement
                    </option>
                    <option value="badge_definition">Badge Definition</option>
                  </select>
                  <span className="text-danger d-flex text-left">
                    <CustomError name="type" form={formik} />
                  </span>
                </div>

                <div className="col-md-6 form-group">
                  <label className="text-left d-flex">
                    {t("Title")} <span className="text-danger">*</span>
                  </label>
                  <input
                    type="text"
                    name="title"
                    className="form-control"
                    placeholder={t("Enter Title")}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    value={formik.values.title}
                  />
                  <span className="text-danger d-flex text-left">
                    <CustomError name="title" form={formik} />
                  </span>
                </div>

                <div className="col-md-12 form-group">
                  <label className="text-left d-flex">{t("Description")}</label>
                  <textarea
                    name="description"
                    className="form-control"
                    placeholder={t("Enter Description")}
                    rows="3"
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    value={formik.values.description}
                  />
                </div>

                <div className="col-md-6 form-group">
                  <label className="text-left d-flex">
                    {t("Reward Type")} <span className="text-danger">*</span>
                  </label>
                  <select
                    name="reward_type"
                    className="form-control"
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    value={formik.values.reward_type}
                  >
                    <option value="">{t("Select Reward Type")}</option>
                    <option value="coins">Coins</option>
                    <option value="stars">Stars</option>
                    <option value="status_update">Status Update</option>
                  </select>
                  <span className="text-danger d-flex text-left">
                    <CustomError name="reward_type" form={formik} />
                  </span>
                </div>

                <div className="col-md-6 form-group">
                  <label className="text-left d-flex">
                    {t("Value")} <span className="text-danger">*</span>
                  </label>
                  <input
                    type="text"
                    name="value"
                    className="form-control"
                    placeholder={t("Enter Value (Number or Status String)")}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    value={formik.values.value}
                  />
                  <span className="text-danger d-flex text-left">
                    <CustomError name="value" form={formik} />
                  </span>
                </div>

                <div className="col-12 mt-4">
                  <h6>{t("Criteria")}</h6>
                  <hr />
                </div>

                {selectedType === "level_completion" && (
                  <>
                    <div className="col-md-6 form-group">
                      <label className="text-left d-flex">
                        {t("Min Score")}
                      </label>
                      <input
                        type="number"
                        name="criteria.min_score"
                        className="form-control"
                        placeholder={t("Enter Min Score")}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        value={formik.values.criteria.min_score}
                      />
                    </div>
                    <div className="col-md-6 form-group">
                      <label className="text-left d-flex">
                        {t("Min Accuracy")}
                      </label>
                      <input
                        type="number"
                        name="criteria.min_accuracy"
                        className="form-control"
                        placeholder={t("Enter Min Accuracy")}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        value={formik.values.criteria.min_accuracy}
                      />
                    </div>
                  </>
                )}

                {selectedType === "streak_bonus" && (
                  <div className="col-md-6 form-group">
                    <label className="text-left d-flex">
                      {t("Streak Days")}
                    </label>
                    <input
                      type="number"
                      name="criteria.streak_days"
                      className="form-control"
                      placeholder={t("Enter Streak Days")}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      value={formik.values.criteria.streak_days}
                    />
                  </div>
                )}

                {selectedType === "milestone_achievement" && (
                  <>
                    <div className="col-md-6 form-group">
                      <label className="text-left d-flex">
                        {t("Total Games")}
                      </label>
                      <input
                        type="number"
                        name="criteria.total_games"
                        className="form-control"
                        placeholder={t("Enter Total Games")}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        value={formik.values.criteria.total_games}
                      />
                    </div>
                    <div className="col-md-6 form-group">
                      <label className="text-left d-flex">
                        {t("Milestone Type")}
                      </label>
                      <input
                        type="text"
                        name="criteria.milestone_type"
                        className="form-control"
                        placeholder={t("Enter Milestone Type")}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        value={formik.values.criteria.milestone_type}
                      />
                    </div>
                  </>
                )}

                <div className="col-12 text-right mt-3">
                  <Link
                    to="/admin/reward-management/reward-config/list/1"
                    className="btn btn-secondary mr-2"
                  >
                    {t("btn_cancel")}
                  </Link>
                  <button
                    type="submit"
                    className="btn btn-main-primary"
                    disabled={loader}
                  >
                    {loader ? (
                      <span
                        className="spinner-border spinner-border-sm mr-2"
                        role="status"
                        aria-hidden="true"
                      ></span>
                    ) : null}
                    {t("btn_submit")}
                  </button>
                </div>
              </div>
            </form>
          </div>
        </div>
      </div>
    </>
  );
};

export default RewardConfigAdd;
